import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, MapPin, Package, Pencil, RotateCcw, Sparkles, Star, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Footer from "../components/Footer";

const EMPTY_ADDR = {
  label: "Casa", name: "", surname: "",
  street: "", zip: "", city: "", province: "", phone: "", is_default: false,
};

const BRAND_LABELS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express" };

function AddressesTab({ user }) {
  const [addresses, setAddresses] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_ADDR, name: user.name || "", surname: user.surname || "" });
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({ ...a });
    setFormOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/account/addresses/${editingId}`, form);
        toast.success("Indirizzo aggiornato");
      } else {
        await api.post("/account/addresses", form);
        toast.success("Indirizzo aggiunto");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'indirizzo "${a.label}"?`)) return;
    try {
      await api.delete(`/account/addresses/${a.id}`);
      toast.success("Indirizzo eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setDefault = async (a) => {
    try {
      await api.post(`/account/addresses/${a.id}/default`);
      toast.success(`"${a.label}" è ora l'indirizzo predefinito`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="addresses-tab">
      <div className="flex items-center justify-between mb-8">
        <p className="text-muted-foreground text-sm tracking-wide">
          {addresses.length === 0
            ? "Nessun indirizzo salvato."
            : `${addresses.length} indirizz${addresses.length === 1 ? "o" : "i"} salvat${addresses.length === 1 ? "o" : "i"}`}
        </p>
        <button
          data-testid="add-address-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
        >
          + Nuovo indirizzo
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        {addresses.map((a) => (
          <div
            key={a.id}
            data-testid={`address-card-${a.id}`}
            className={`border p-7 transition-colors duration-300 ${
              a.is_default ? "border-primary/60 bg-primary/5" : "border-white/10 bg-card"
            }`}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary">
                <MapPin size={13} /> {a.label}
              </span>
              {a.is_default && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-primary">Predefinito</span>
              )}
            </div>
            <p className="font-serif text-xl mb-1">{a.name} {a.surname}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {a.street}<br />
              {a.zip} {a.city} ({a.province})
              {a.phone && <><br />{a.phone}</>}
            </p>
            <div className="flex items-center gap-2 mt-6">
              <button
                data-testid={`address-edit-${a.id}`}
                onClick={() => openEdit(a)}
                className="p-2 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Modifica indirizzo"
              >
                <Pencil size={15} />
              </button>
              <button
                data-testid={`address-delete-${a.id}`}
                onClick={() => remove(a)}
                className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                aria-label="Elimina indirizzo"
              >
                <Trash2 size={15} />
              </button>
              {!a.is_default && (
                <button
                  data-testid={`address-default-${a.id}`}
                  onClick={() => setDefault(a)}
                  className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
                >
                  <Star size={13} /> Imposta predefinito
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {formOpen && (
        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          onSubmit={submit}
          className="border border-white/10 bg-card p-8 space-y-5 max-w-2xl"
          data-testid="address-form"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
            {editingId ? "Modifica indirizzo" : "Nuovo indirizzo"}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Etichetta (es. Casa, Lavoro)</Label>
              <Input data-testid="address-label" required value={form.label} onChange={set("label")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Telefono</Label>
              <Input data-testid="address-phone" value={form.phone} onChange={set("phone")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nome</Label>
              <Input data-testid="address-name" required value={form.name} onChange={set("name")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cognome</Label>
              <Input data-testid="address-surname" required value={form.surname} onChange={set("surname")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Via e numero civico</Label>
            <Input data-testid="address-street" required value={form.street} onChange={set("street")}
              className="bg-secondary border-white/10 focus-visible:ring-primary" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CAP</Label>
              <Input data-testid="address-zip" required value={form.zip} onChange={set("zip")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Città</Label>
              <Input data-testid="address-city" required value={form.city} onChange={set("city")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Provincia</Label>
              <Input data-testid="address-province" required maxLength={50} value={form.province} onChange={set("province")}
                className="bg-secondary border-white/10 focus-visible:ring-primary" />
            </div>
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={busy}
              data-testid="address-form-submit"
              className="h-12 px-8 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Salvataggio…" : "Salva indirizzo"}
            </button>
            <button
              type="button"
              data-testid="address-form-cancel"
              onClick={() => setFormOpen(false)}
              className="h-12 px-8 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-300"
            >
              Annulla
            </button>
          </div>
        </motion.form>
      )}
    </div>
  );
}

function CardsTab() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/account/cards")
      .then((r) => setCards(r.data.cards || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la carta •••• ${c.last4}?`)) return;
    try {
      await api.delete(`/account/cards/${c.id}`);
      toast.success("Carta eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setDefault = async (c) => {
    try {
      await api.post(`/account/cards/${c.id}/default`);
      toast.success("Carta predefinita aggiornata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="cards-tab">
      {loading ? (
        <p className="text-muted-foreground text-sm">Caricamento…</p>
      ) : cards.length === 0 ? (
        <div className="border border-white/10 bg-card p-10 max-w-xl" data-testid="cards-empty">
          <CreditCard size={22} className="text-primary mb-5" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm leading-relaxed">
            Nessuna carta salvata. Al prossimo ordine, spunta{" "}
            <span className="text-foreground">«Salva questa carta per i prossimi acquisti»</span>{" "}
            al checkout: la carta verrà salvata in modo sicuro su Stripe (i dati non passano mai
            dai nostri server).
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
          {cards.map((c) => (
            <div
              key={c.id}
              data-testid={`card-item-${c.id}`}
              className={`border p-7 transition-colors duration-300 ${
                c.is_default ? "border-primary/60 bg-primary/5" : "border-white/10 bg-card"
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <CreditCard size={20} className="text-primary" strokeWidth={1.5} />
                {c.is_default && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-primary">Predefinita</span>
                )}
              </div>
              <p className="font-serif text-xl capitalize mb-1">
                {BRAND_LABELS[c.brand] || c.brand} •••• {c.last4}
              </p>
              <p className="text-sm text-muted-foreground">
                Scadenza {String(c.exp_month).padStart(2, "0")}/{c.exp_year}
              </p>
              <div className="flex items-center gap-2 mt-6">
                <button
                  data-testid={`card-delete-${c.id}`}
                  onClick={() => remove(c)}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Elimina carta"
                >
                  <Trash2 size={15} />
                </button>
                {!c.is_default && (
                  <button
                    data-testid={`card-default-${c.id}`}
                    onClick={() => setDefault(c)}
                    className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Star size={13} /> Imposta predefinita
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ORDER_STATUS = {
  paid: "Pagato",
  pending: "In attesa",
  expired: "Scaduto",
  failed: "Non riuscito",
  refunded: "Rimborsato",
};

const RETURN_REASONS = ["Taglia errata", "Prodotto difettoso", "Non mi piace", "Altro"];

function displayStatus(o) {
  if (o.payment_status !== "paid") return ORDER_STATUS[o.payment_status] || o.payment_status;
  const f = o.fulfillment_status;
  if (f === "shipped") return "Spedito";
  if (f === "delivered") return "Consegnato";
  if (f === "return_requested") return "Richiesta di reso in attesa";
  if (f === "return_approved") return "Reso approvato — in attesa di rientro";
  return "Pagato";
}

function returnWindowOpen(o) {
  const base = o.delivered_at || o.created_at;
  if (!base) return true;
  return (Date.now() - new Date(base).getTime()) / 86400000 <= 14;
}

function ReturnModal({ order, open, onClose, onDone }) {
  const [selected, setSelected] = useState({});
  const [reasons, setReasons] = useState({});
  const [notes, setNotes] = useState({});
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [aiBusyFor, setAiBusyFor] = useState("");

  const suggestWithAI = async (itemName) => {
    setAiBusyFor(itemName);
    setError("");
    try {
      const { data } = await api.post("/ai/return-suggestion", {
        product_name: itemName,
        note: notes[itemName] || "",
      });
      setReasons((r) => ({ ...r, [itemName]: data.reason }));
      setNotes((n) => ({ ...n, [itemName]: data.note }));
      toast.success("Suggerimento AI applicato");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAiBusyFor("");
    }
  };

  const toggle = (name) => {
    setSelected((s) => ({ ...s, [name]: !s[name] }));
    setReasons((r) => (r[name] ? r : { ...r, [name]: "Non mi piace" }));
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setError("Foto troppo grande (max 2 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    const items = (order.items || [])
      .filter((i) => selected[i.name])
      .map((i) => ({
        name: i.name,
        quantity: i.quantity,
        reason: reasons[i.name] || "Altro",
        note: notes[i.name] || "",
      }));
    if (!items.length) {
      setError("Seleziona almeno un articolo da restituire.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/returns", { order_id: order.id, items, photo });
      toast.success("Richiesta di reso inviata");
      onDone();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#121214] border-white/10 text-foreground max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="return-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif font-light text-2xl">
            Richiesta di reso — ordine #{order.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          {(order.items || []).map((i) => (
            <div key={i.name} className="border border-white/10 p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={!!selected[i.name]}
                  onCheckedChange={() => toggle(i.name)}
                  data-testid={`return-check-${i.name}`}
                  className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-sm">{i.name} ×{i.quantity}</span>
              </label>
              {selected[i.name] && (
                <div className="mt-4 space-y-3">
                  <Select
                    value={reasons[i.name] || "Non mi piace"}
                    onValueChange={(v) => setReasons({ ...reasons, [i.name]: v })}
                  >
                    <SelectTrigger className="bg-secondary border-white/10" data-testid={`return-reason-${i.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-white/10 text-foreground">
                      {RETURN_REASONS.map((r) => (
                        <SelectItem key={r} value={r} className="focus:bg-white/5 focus:text-foreground">
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Note (facoltative)"
                    value={notes[i.name] || ""}
                    onChange={(e) => setNotes({ ...notes, [i.name]: e.target.value })}
                    className="bg-secondary border-white/10"
                    data-testid={`return-note-${i.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => suggestWithAI(i.name)}
                    disabled={aiBusyFor === i.name}
                    data-testid={`return-ai-${i.name}`}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    <Sparkles size={12} /> {aiBusyFor === i.name ? "L'AI sta pensando…" : "Suggerisci motivo con AI"}
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Carica foto (facoltativa, se prodotto difettoso)
            </Label>
            <Input
              type="file"
              accept="image/*"
              onChange={onPhoto}
              data-testid="return-photo-input"
              className="bg-secondary border-white/10"
            />
            {photo && <p className="text-xs text-primary">Foto allegata</p>}
          </div>
          {error && <p className="text-sm text-destructive" data-testid="return-error">{error}</p>}
          <button
            onClick={submit}
            disabled={busy}
            data-testid="return-submit"
            className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? "Invio…" : "Invia richiesta di reso"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [returnFor, setReturnFor] = useState(null);

  const load = useCallback(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (orders === null) {
    return <p className="text-muted-foreground text-sm">Caricamento…</p>;
  }

  if (orders.length === 0) {
    return (
      <div className="border border-white/10 bg-card p-10 max-w-xl" data-testid="orders-empty">
        <Package size={22} className="text-primary mb-5" strokeWidth={1.5} />
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          Nessun ordine ancora. La collezione ti aspetta.
        </p>
        <Link to="/shop" className="text-primary text-sm tracking-wide" data-testid="orders-shop-link">
          Scopri la collezione
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="orders-list">
      {orders.map((o) => {
        const f = o.fulfillment_status || "";
        const hasOpenReturn = !!o.return_status || ["return_requested", "return_approved"].includes(f);
        const canReturn = o.payment_status === "paid" && !hasOpenReturn && returnWindowOpen(o);
        const expired = o.payment_status === "paid" && !hasOpenReturn && !returnWindowOpen(o);
        return (
          <div key={o.id} className="border border-white/10 bg-card p-7" data-testid={`order-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <span className="text-xs uppercase tracking-[0.25em] text-primary">
                Ordine #{o.id.slice(0, 8).toUpperCase()}
              </span>
              <span
                className={`text-[11px] uppercase tracking-[0.2em] ${
                  o.payment_status === "paid" ? "text-primary" : "text-muted-foreground"
                }`}
                data-testid={`order-status-${o.id}`}
              >
                {displayStatus(o)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              {(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT") : ""}
              </span>
              <span className="font-serif text-xl">{eur(o.total || 0)}</span>
            </div>

            {f === "shipped" && o.tracking_link && (
              <div className="mt-5 border border-white/10 p-5" data-testid={`tracking-${o.id}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                  Numero tracking:{" "}
                  <span className="text-foreground">{o.courier} {o.tracking_number}</span>
                </p>
                <a
                  href={o.tracking_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`track-btn-${o.id}`}
                  className="inline-flex items-center justify-center gap-3 w-full py-4 rounded-full bg-[#2E7D4F] hover:bg-[#37925e] text-white text-xs uppercase tracking-[0.25em] font-medium transition-colors duration-300"
                >
                  <Truck size={15} /> Traccia il tuo pacco
                </a>
              </div>
            )}

            <div className="mt-5">
              {canReturn && (
                <button
                  onClick={() => setReturnFor(o)}
                  data-testid={`return-btn-${o.id}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
                >
                  <RotateCcw size={13} /> Restituisci articolo/i
                </button>
              )}
              {expired && (
                <p className="text-xs text-muted-foreground/70" data-testid={`return-expired-${o.id}`}>
                  Periodo di reso scaduto (14gg)
                </p>
              )}
              {o.return_status === "requested" && (
                <p className="text-xs text-primary">Richiesta di reso in attesa</p>
              )}
              {o.return_status === "approved" && (
                <p className="text-xs text-primary">Reso approvato — in attesa di rientro</p>
              )}
            </div>
          </div>
        );
      })}
      {returnFor && (
        <ReturnModal
          order={returnFor}
          open={!!returnFor}
          onClose={() => setReturnFor(null)}
          onDone={load}
        />
      )}
    </div>
  );
}

export default function Account() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const current = tab === "metodi-di-pagamento" ? "carte" : tab === "ordini" ? "ordini" : "indirizzi";

  if (user === null) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-muted-foreground">Verifica accesso…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="account-login-required">
        <h1 className="font-serif font-light text-4xl mb-6">Il tuo account</h1>
        <p className="text-muted-foreground mb-8">Accedi per gestire indirizzi e metodi di pagamento.</p>
        <Link to="/auth" className="text-primary text-sm tracking-wide" data-testid="account-login-link">
          Vai al login
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="account-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-14 md:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="overline-label mb-5">{user.name} {user.surname}</p>
          <h1 className="font-serif font-light text-4xl md:text-6xl mb-14">
            Il tuo <span className="italic text-primary">account</span>
          </h1>
        </motion.div>

        <Tabs
          value={current}
          onValueChange={(v) =>
            navigate(
              v === "carte"
                ? "/account/metodi-di-pagamento"
                : v === "ordini"
                  ? "/account/ordini"
                  : "/account/indirizzi"
            )
          }
        >
          <TabsList className="bg-secondary border border-white/10 mb-12">
            <TabsTrigger value="indirizzi" className="text-xs uppercase tracking-[0.2em]" data-testid="tab-addresses">
              I miei indirizzi
            </TabsTrigger>
            <TabsTrigger value="ordini" className="text-xs uppercase tracking-[0.2em]" data-testid="tab-orders">
              I miei ordini
            </TabsTrigger>
            <TabsTrigger value="carte" className="text-xs uppercase tracking-[0.2em]" data-testid="tab-cards">
              Metodi di pagamento
            </TabsTrigger>
          </TabsList>
          <TabsContent value="indirizzi">
            <AddressesTab user={user} />
          </TabsContent>
          <TabsContent value="ordini">
            <OrdersTab />
          </TabsContent>
          <TabsContent value="carte">
            <CardsTab />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </main>
  );
}
